import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfigurationService, PathsService, UserManagementService, CoursesService } from '@draeger-academy/common';
import { User } from 'models/user';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { FormsUtils } from '../form-builder';
import { Location } from '@angular/common';

@Component({
    selector: 'da-personal-data-form',
    templateUrl: './personal-data-form.component.html',
    styleUrls: ['./personal-data-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PersonalDataFormComponent implements OnInit {
    public personalForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    hasFocusTitle = false;
    hasFocusDepartment = false;
    hasFocusPhone = false;
    hasFocusPhoneNumber = false;
    hasFocusPhoneCity = false;
    hasFocusPhoneCountry = false;
    hasFocusAddress = false;
    isEditMode = false;
    hasFocusAddressAdditional = false;
    hasFocusCity = false;
    hasFocusSalutation = false;
    showError = false;
    user: User = new User([]);
    companyCountryName: string;
    salutationData: any;
    countriesData: any;
    private _currentData = null;
    private _subscription: Subscription;
    public showBackButton = false;
    public isPortal= false;
    private _$companyCountryName: BehaviorSubject<string> = new BehaviorSubject('null');
    private _$companyRegionName: BehaviorSubject<string> = new BehaviorSubject('null');

    private _$user: BehaviorSubject<User> = new BehaviorSubject(null);
    public get user$(): Observable<User> {
        return this._$user.asObservable();
    }

    private _$portalId: BehaviorSubject<string> = new BehaviorSubject('');
    public get portalId$(): Observable<string> {
        return this._$portalId.asObservable();
    }

    public get companyRegionName$(): Observable<string> {
        return this._$companyRegionName.asObservable();
    }

    private _$billingContact: BehaviorSubject<User> = new BehaviorSubject(null);
    public _$showChangePassword: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _$showError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private _$countries: BehaviorSubject<any> = new BehaviorSubject<any>({});

    private _$salutations: BehaviorSubject<any> = new BehaviorSubject<any>({});
    public get salutations$(): Observable<{}> {
        return this._$salutations.asObservable();
    }

    editTitle = true;
    linkChangePassword: any;
    linkBookingSteps: string;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private userService: UserManagementService,
        private usermanagement: UserManagementService,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService,
        private location: Location,
        private _courseService: CoursesService
    ) {}

    ngOnInit() {
        this._configurationService.$allCountriesByLanguage.subscribe(data => {
            this._getSalutaionAndCountries();
            this.setForm();
        })
        this._courseService.languageSelected.subscribe((language) => {
            this._getSalutaionAndCountries();
            this.setForm();
        });
        this.linkBookingSteps = this._pathsService.getCheckoutLink();
        this.linkChangePassword = this._pathsService.getAccountChangePasswordLink();
        this.personalForm = this.formBuilder.group({
            title: [''],
            department: [''],
            phone: [''],
            phoneNumber: [''],
            phoneCity: [''],
            phoneCountry: [''],
            subscribe: false,
            salutation: ['', Validators.required]
        });

        this._getSalutaionAndCountries();

        this.setForm();

        if (this._configurationService.hasFeature('ChangePassword')) {
            this._$showChangePassword.next(true);
        }
        this.checkFeaturesOnPortals();
        this._subscription = this.route.queryParams.subscribe((params) => {
            this.showBackButton = Boolean(params['booking']) ? true : false;
        });
    }

    get f() {
        return this.personalForm.controls;
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
        this.submitted = true;
        if (this.personalForm.invalid) {
            return;
        }
        const _user = this._$user.getValue();
        _user.title = this.f.title.value;
        _user.department = this.f.department.value;
        _user.phoneCity = this.f.phoneCity.value;
        _user.phoneCountry = this.f.phoneCountry.value;
        _user.phoneNumber = this.f.phoneNumber.value;
        _user.phone = this.f.phoneCountry.value + this.f.phoneCity.value + this.f.phoneNumber.value;
        _user.salutation.name = this.f.salutation.value;
        _user.salutation.code = this.f.salutation.value;
        _user.subscribe = this.f.subscribe.value;
        this.loading = true;

        this.usermanagement.updateUser(_user).then(
            (user) => {
                if (user) {
                    this.f.title.setValue(user.title);
                    this.f.department.setValue(user.department);
                    this.f.phone.setValue(user.phone);
                    this.f.phoneCity.setValue(user.phoneCity);
                    this.f.phoneCountry.setValue(user.phoneCountry);
                    this.f.phoneNumber.setValue(user.phoneNumber);
                    this.f.salutation.setValue(user.salutation.name);
                    this.f.subscribe.setValue(user.subscribe);
                }
                this.loading = false;
                this.isEditMode = false;
                user.salutation = user.salutation;
                this.translateSalutationsAndCountries(user);
                this._$user.next(user);
                window.scrollTo(0, 0);
                // window.location.href = this.returnUrl;
                // TO DO: header is not updating on the redirect - and login link is not hidden once we log in until we refresh the page
            },
            (error) => {
                console.log(error);
                this._$showError.next(true);
                this.loading = false;
                window.scrollTo(0, 0);
            }
        );
    }

    private setForm() {
        this.userService.getUser().then((user) => {
            if (user) {
                this._$user.next(user);
                this.f.title.setValue(user.title);
                this.f.department.setValue(user.department);
                this.f.phone.setValue(user.phone);
                this.f.phoneNumber.setValue(user.phoneNumber);
                this.f.phoneCity.setValue(user.phoneCity);
                this.f.phoneCountry.setValue(user.phoneCountry);
                this.f.salutation.setValue(user.salutation.code);
                this.f.subscribe.setValue(user.subscribe);
                this._$user.next(user);
                this.translateSalutationsAndCountries(user);
                if (user.billingContactId === user.id) {
                    this._$billingContact.next(user);
                } else {
                    this.userService.getBillingContact(user.billingContactId).then((useR) => {
                        if (useR) {
                            this._$billingContact.next(useR);
                        }
                    });
                }
            }
        });
    }
    goBack() {
        this.location.back();
    }

    cancel() {
        this.isEditMode = false;
        window.scrollTo(0, 0);
    }

    edit() {
        this.isEditMode = true;
        window.scrollTo(0, 0);
    }

    // ngOnDestroy() {
    //     this._subscription.unsubscribe();
    // }

    checkFeaturesOnPortals() {
        this.isPortal = this._configurationService.isPartnerPage();

        if (this.isPortal) {
            this._configurationService.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                    let hasChangePassword;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasChangePassword = tempFeatures[0].value.filter((setting) => setting.name === 'ChangePassword');
                    }
                    if (hasChangePassword && hasChangePassword[0] && hasChangePassword[0].enabled) {
                        this._$showChangePassword.next(true);
                    }
                }
            });
        }
        this._configurationService.currentPortal$.subscribe((portalId) => {
            this._$portalId.next(portalId);
        });
    }

    selectSalutation(value) {
        this.personalForm.get('salutation').setValue(value);
    }

    private _getSalutaionAndCountries() {
        this.salutationData = this._configurationService.$allSalutationsByLanguage.getValue();
        this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();
        this._$salutations.next(this.salutationData);
        this._$countries.next(this.countriesData);
    }

    private translateSalutationsAndCountries(user) {
        this._getSalutaionAndCountries();
        let salutation;
        if (user.salutation.code === '' || user.salutation.code === null || !user.salutation.code) {
            salutation = null;
        } else {
            salutation = this.salutationData.filter((item) => item.key === user.salutation.code);
        }
        let salutationBilling;
        if (
            user.billingContact.salutation.code === '' ||
            user.billingContact.salutation.code === null ||
            !user.billingContact.salutation.code
        ) {
            salutationBilling = null;
        } else {
            salutationBilling = this.salutationData.filter((item) => item.key === user.billingContact.salutation.code);
        }
        const country = this.countriesData.filter((item) => item.key === user.country.code);
        const countryBilling = this.countriesData.filter((item) => item.key === user.billingContact.country.code);
        if (salutation !== null) {
            user.salutation.name = salutation[0]?.value;
        } else {
            user.salutation.name = null;
        }
        if (salutationBilling !== null) {
            user.billingContact.salutation.name = salutationBilling[0]?.value;
        } else {
            user.billingContact.salutation.name = null;
        }
        user.country.name = country[0]?.value;
        user.billingContact.country.name = countryBilling[0]?.value;
        this._$user.next(user);
    }
}
